import React from 'react';
import { 
  Users, ShieldCheck, Zap, Globe, Award, Clock, 
  BarChart3, BadgeCheck, Rocket, Briefcase, 
  Wallet, Cpu, MessageSquare, Microscope
} from 'lucide-react';

const EliteNetworkDashboard = () => {
  // Mock aggregated data based on CandidateProfileEntity fields
  const metrics = {
    talentPool: "4.2k+",
    trustScore: 98.4,
    seniority: [
      { label: 'Architect / Principal', value: 32 },
      { label: 'Staff Engineer', value: 38 },
      { label: 'Senior / Lead', value: 30 },
    ],
    topReputation: [
      { label: 'Open Source Contributors', count: '120+', icon: <Cpu size={16}/> },
      { label: 'Patent Holders', count: '45', icon: <Microscope size={16}/> },
      { label: 'Conference Speakers', count: '85', icon: <MessageSquare size={16}/> },
    ],
    marketContext: {
      avgNoticePeriod: "18 Days",
      remotePreference: "72%",
      avgExp: "9.4 Years"
    },
    impactClusters: [
      "Reduced Infrastructure Costs ($2M+ Combined)",
      "Scaled Systems to 10M+ Transactions/Day",
      "92% Success in Microservices Migration"
    ]
  };

  return (
    <div className="bg-white min-h-screen p-6 lg:p-12 font-sans text-slate-900">
      <div className="max-w-7xl mx-auto">
        
        {/* HEADER: The Value Prop */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 gap-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
              <BadgeCheck size={14} /> Recruiter Intelligence Portal
            </div>
            {/* The talents page already opens with an h1, so this section heading stays an h2 and
                the page keeps a single top level heading. */}
            <h2 className="text-4xl lg:text-5xl font-black tracking-tight text-slate-900 mb-4">
              The <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-500">Reverse Talent</span> Network
            </h2>
            <p className="text-slate-500 text-lg leading-relaxed">
              Skip the screening. Our network consists of candidates with proven business impact, 
              verified leadership scores, and high-scale architecture experience.
            </p>
          </div>
          
          <div className="flex gap-4">
            <div className="bg-slate-900 text-white p-6 rounded-2xl shadow-xl min-w-[200px]">
              <div className="text-3xl font-black">94%</div>
              <div className="text-slate-400 text-xs uppercase font-bold tracking-tighter">Profile Match Rate</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          
          {/* COLUMN 1: Technical Depth & Scale (utilizing architectureExperience & productScale) */}
          <div className="md:col-span-4 space-y-6">
            <div className="bg-slate-50 border border-slate-200 p-6 rounded-3xl">
              <h3 className="flex items-center gap-2 font-bold text-slate-800 mb-6">
                <Globe className="text-blue-600" size={20}/> Engineering Scale
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-slate-100 shadow-sm">
                  <span className="text-sm font-medium text-slate-600">Internet Scale (10M+)</span>
                  <span className="bg-blue-100 text-blue-700 font-bold px-2 py-1 rounded-lg text-xs">850 Candidates</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-slate-100 shadow-sm">
                  <span className="text-sm font-medium text-slate-600">Event-Driven Systems</span>
                  <span className="bg-indigo-100 text-indigo-700 font-bold px-2 py-1 rounded-lg text-xs">1.2k Candidates</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-slate-100 shadow-sm">
                  <span className="text-sm font-medium text-slate-600">Microservices Mastery</span>
                  <span className="bg-emerald-100 text-emerald-700 font-bold px-2 py-1 rounded-lg text-xs">2.4k Candidates</span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-indigo-600 to-blue-700 p-6 rounded-3xl text-white shadow-lg">
              <h3 className="flex items-center gap-2 font-bold mb-6">
                <Award className="text-blue-200" size={20}/> Reputation Signals
              </h3>
              <div className="grid grid-cols-1 gap-3">
                {metrics.topReputation.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white/10 p-3 rounded-xl backdrop-blur-sm">
                    <div className="flex items-center gap-2 text-sm">
                      {item.icon} {item.label}
                    </div>
                    <span className="font-bold">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* COLUMN 2: Leadership & Cultural Fit (utilizing roleLevel, workStyle, hiringAuthority) */}
          <div className="md:col-span-5 space-y-6">
            <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm">
              <h3 className="flex items-center gap-2 font-bold text-slate-800 mb-6">
                <ShieldCheck className="text-emerald-500" size={20}/> High-Authority Talent
              </h3>
              <div className="space-y-5">
                {metrics.seniority.map((s, i) => (
                  <div key={i}>
                    <div className="flex justify-between text-sm font-bold mb-2">
                      <span>{s.label}</span>
                      <span>{s.value}%</span>
                    </div>
                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600" 
                        style={{ width: `${s.value}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-2xl">
                  <div className="text-xs font-bold text-slate-400 uppercase mb-1">Ownership</div>
                  <div className="text-sm font-bold">Platform/Product Owners</div>
                </div>
                <div className="p-4 bg-slate-50 rounded-2xl">
                  <div className="text-xs font-bold text-slate-400 uppercase mb-1">Work Style</div>
                  <div className="text-sm font-bold">Innovation & Scale Focused</div>
                </div>
              </div>
            </div>

            <div className="bg-slate-900 p-6 rounded-3xl text-white">
               <h3 className="flex items-center gap-2 font-bold mb-4">
                <Zap className="text-amber-400" size={20}/> Business Impact Ledger
              </h3>
              <div className="space-y-4">
                {metrics.impactClusters.map((impact, i) => (
                  <div key={i} className="flex gap-3 text-sm text-slate-300">
                    <div className="h-2 w-2 rounded-full bg-amber-400 mt-1.5 shrink-0" />
                    {impact}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* COLUMN 3: Hiring Velocity & Compensation (utilizing decisionSpeed, noticePeriod, compensation) */}
          <div className="md:col-span-3 space-y-6">
            <div className="bg-amber-50 border border-amber-100 p-6 rounded-3xl">
              <h3 className="flex items-center gap-2 font-bold text-amber-800 mb-6">
                <Rocket className="text-amber-600" size={20}/> Market Readiness
              </h3>
              <div className="space-y-6">
                <div>
                  <div className="text-3xl font-black text-amber-900">{metrics.marketContext.avgNoticePeriod}</div>
                  <div className="text-xs font-bold text-amber-700 uppercase tracking-tighter">Average Notice Period</div>
                </div>
                <div>
                  <div className="text-3xl font-black text-amber-900">{metrics.marketContext.remotePreference}</div>
                  <div className="text-xs font-bold text-amber-700 uppercase tracking-tighter">Prefer Remote/Hybrid</div>
                </div>
                <div className="pt-4 border-t border-amber-200">
                  <div className="flex items-center gap-2 text-sm font-bold text-amber-900">
                    <Clock size={16}/> 85% Active Explorers
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm">
              <h3 className="flex items-center gap-2 font-bold text-slate-800 mb-4">
                <Wallet className="text-slate-600" size={20}/> Comp. Benchmarks
              </h3>
              <div className="text-sm text-slate-500 mb-4 italic">
                Get real-time expected vs current compensation data.
              </div>
              <button className="w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-xl text-sm font-bold transition-colors">
                View Salary Insights
              </button>
            </div>
          </div>

        </div>

        {/* FOOTER CTA */}
        <div className="mt-12 bg-blue-600 rounded-3xl p-8 text-center text-white shadow-2xl shadow-blue-200">
          <h2 className="text-2xl font-bold mb-2">Ready to hire your next engineering leader?</h2>
          <p className="text-blue-100 mb-8 max-w-lg mx-auto">Access the full dashboard of 4,000+ candidates with verified technical scores and leadership ratings.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button className="px-8 py-4 bg-white text-blue-600 rounded-2xl font-bold hover:shadow-lg transition-all">
              Create Recruiter Account
            </button>
            <button className="px-8 py-4 bg-blue-700 text-white rounded-2xl font-bold hover:bg-blue-800 transition-all border border-blue-500">
              Schedule Platform Demo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EliteNetworkDashboard;